import React from 'react';
import { Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { useAuth } from '../../context/AuthProvider';
import { UserRole } from '../../api/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  redirectTo?: string;
}

/**
 * ProtectedRoute component
 * Wraps routes that require authentication and optionally specific roles
 *
 * @param children - Component to render if authorized
 * @param allowedRoles - Optional array of roles allowed to access this route
 * @param redirectTo - Optional redirect path (default: '/login')
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
  redirectTo = '/login',
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Not authenticated, redirect to login
  if (!isAuthenticated) {
    console.debug('[ProtectedRoute] User not authenticated, redirecting to', redirectTo);
    return <Navigate to={redirectTo} replace />;
  }

  // Check role-based access if allowedRoles specified
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    console.warn('[ProtectedRoute] User role not authorized', {
      userRole: user.role,
      allowedRoles,
    });
    return <Navigate to="/unauthorized" replace />;
  }

  // Authenticated and authorized, render children
  return <>{children}</>;
};
